import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'pg19_theme'
const MODE_KEY = 'pg19_theme_mode'
const DARK_QUERY = '(prefers-color-scheme: dark)'

type ThemeValue = 'dark' | 'light'
type ThemeMode = 'system' | 'manual'

function readStoredTheme(): ThemeValue | null {
  if (typeof window === 'undefined') return null

  const raw = window.localStorage.getItem(STORAGE_KEY)
  return raw === 'dark' || raw === 'light' ? raw : null
}

function readThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system'
  return window.localStorage.getItem(MODE_KEY) === 'manual' ? 'manual' : 'system'
}

function readSystemDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }

  return window.matchMedia(DARK_QUERY).matches
}

function applyThemeToDocument(isDark: boolean) {
  document.documentElement.classList.toggle('dark', isDark)
}

function persistTheme(isDark: boolean) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light')
}

export function useThemeDetect() {
  const hasManualPreferenceRef = useRef(readThemeMode() === 'manual')
  const [isDark, setIsDark] = useState(() => {
    if (hasManualPreferenceRef.current) {
      const stored = readStoredTheme()
      if (stored !== null) return stored === 'dark'
    }
    return readSystemDark()
  })

  useEffect(() => {
    applyThemeToDocument(isDark)
  }, [isDark])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }

    const media = window.matchMedia(DARK_QUERY)
    const onChange = (event: MediaQueryListEvent) => {
      if (hasManualPreferenceRef.current) return
      setIsDark(event.matches)
    }

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange)
    } else {
      media.addListener(onChange)
    }

    return () => {
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', onChange)
      } else {
        media.removeListener(onChange)
      }
    }
  }, [])

  const toggle = useCallback(() => {
    hasManualPreferenceRef.current = true
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(MODE_KEY, 'manual')
    }
    setIsDark((prev) => {
      const next = !prev
      persistTheme(next)
      return next
    })
  }, [])

  return { isDark, toggle }
}
